import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { getFeaturePermission } from '@zeta-commons';
import {
  FullPageSpinner,
  enqueueSnackbar,
  useDefaultEnumsStore,
  useFactFindConfigStore,
  useProfileProgressStore,
  useSubscriptionDetailsStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { AdvisorAPIs, getErrorMessage, InvestorService, QueryAPIs } from '@zeta-services';
import { RouterLinks } from '../app-route-paths';
import AccessGuard from './access-guard';
import { useFlexiblePlanAccess } from '../hooks/use-flexible-plan-access';

type Props = {
  children: React.ReactNode;
};

const ProtectedRoute = ({ children }: Props) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const userAccessLoading = useUserAccessStore((state) => state.loading);
  const subscription = useSubscriptionDetailsStore((state) => state.subscription);
  const subscriptionLoading = useSubscriptionDetailsStore((state) => state.loading);
  const navigate = useNavigate();
  const location = useLocation();
  const isOrgAccessible = getFeaturePermission(userAccess?.access, 'AdvisorOrgConfig').accessible;
  const { role, orgRole } = useUserDetailsStore((state) => state.user);
  const profileProgressStore = useProfileProgressStore((state) => state);
  const updateFactFindConfigDetails = useFactFindConfigStore((state) => state);
  const updateDefaultEnums = useDefaultEnumsStore((state) => state);
  const { hasFlexiblePlanAccess, isFlexiblePlanAccessLoading } = useFlexiblePlanAccess();

  const [loading, setLoading] = useState(true);

  const checkProfileSetupStatus = async () => {
    if (subscription || hasFlexiblePlanAccess) {
      const res = await QueryAPIs().profileSetupProgress({});
      profileProgressStore.updateProfileSetupStatus(res?.data, true);
    }
  };

  const viewFFConfigAPI = async () => {
    const res = await AdvisorAPIs().viewFactFindConfig({});
    updateFactFindConfigDetails.updateFactFindConfigDetails(res?.data?.response, true);
  };

  const getDefaultEnumsData = async () => {
    const res = await InvestorService().getDefaultEnums({});
    updateDefaultEnums.updateDefaultEnums(res?.data?.response, true);
  };

  const startupReadOptions = {
    retry: (failureCount: number, error: any) => failureCount < 1 && (!error?.response || error.response.status >= 500),
    onError: (error: unknown) => enqueueSnackbar(getErrorMessage(error) || 'Could not load startup data. Reload to retry.', { variant: 'error' }),
  };
  const profileSetupRead = useMutation({ mutationFn: checkProfileSetupStatus, ...startupReadOptions });
  const factFindConfigRead = useMutation({ mutationFn: viewFFConfigAPI, ...startupReadOptions });
  const defaultEnumsRead = useMutation({ mutationFn: getDefaultEnumsData, ...startupReadOptions });

  useEffect(() => {
    if (
      !updateFactFindConfigDetails.status &&
      (location.pathname.includes('fact-find') || location.pathname.includes('para-planner') || location.pathname.includes('managed-client')) &&
      isOrgAccessible
    )
      factFindConfigRead.mutate();
    if (role && profileProgressStore.status) {
      setLoading(false);
      // const rolePathData: any = UserRolePaths[checkUserRole(role, orgRole, profileProgressStore?.progressData?.firmProfileSetupComplete)];
      // if (rolePathData?.deniedPaths.some((path: any) => location.pathname.startsWith(path))) {
      //   console.log('rolePathData', rolePathData);
      //   return navigate(RouterLinks.forbidden, { replace: true });
      // }
    } else {
      setLoading(true);
      if (!profileProgressStore.status && !subscriptionLoading && !isFlexiblePlanAccessLoading) profileSetupRead.mutate();
    }

    if (!updateDefaultEnums.status) defaultEnumsRead.mutate();
  }, [role, orgRole, profileProgressStore.status, subscriptionLoading, hasFlexiblePlanAccess, isFlexiblePlanAccessLoading]);

  // Wait for subscription data to load before checking subscription status
  if (subscriptionLoading || userAccessLoading || isFlexiblePlanAccessLoading) {
    return <FullPageSpinner />;
  }

  if (!subscription && !hasFlexiblePlanAccess) {
    // Redirect to pricing page if no subscription, except if already on pricing page
    if (!location.pathname.includes('/pricing') && !location.pathname.includes('/complete-payment')) {
      navigate(RouterLinks.pricing, { replace: true });
      return <FullPageSpinner />;
    }
    return <AccessGuard>{children}</AccessGuard>;
  }

  // If we have a subscription but other data is still loading, show children anyway
  // since subscription is the main requirement for accessing protected routes
  if (loading && !role) {
    return <FullPageSpinner />;
  }

  return <AccessGuard>{children}</AccessGuard>;
};

export default ProtectedRoute;
